import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";
import "./Public.css";

const pubUrl = process.env.PUBLIC_URL || "";

const sidingServices = [
  {
    title: "Siding Replacement",
    body: "Replacement scopes for worn, damaged, faded, or failing siding systems with attention to weather protection and exterior finish.",
  },
  {
    title: "Siding Repairs",
    body: "Targeted repair work for loose panels, localized damage, exposed areas, and problem spots that need correction before they spread.",
  },
  {
    title: "Trim & Detail Work",
    body: "Exterior trim, transitions, edges, and finishing details that help siding look complete and function properly.",
  },
];

const priorities = [
  "Weather control at vulnerable transitions",
  "Clean lines around windows, doors, and corners",
  "Material selection that fits the building",
  "A finished exterior that improves curb appeal",
];

export default function Siding() {
  useSeo({
    title: "Siding | Crowned Roofing",
    description:
      "Siding replacement, siding repairs, trim work, and exterior finishing services focused on clean detail work and durable weather protection.",
    ogImage: "https://www.crownedroofing.com/images/ogimg.png",
    canonical: "https://www.crownedroofing.com/siding",
  });

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Siding | Crowned Roofing";
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute(
        "content",
        "Siding replacement, siding repairs, trim work, and exterior finishing services focused on clean detail work and durable weather protection."
      );
    }
  }, []);

  return (
    <main className="publicPage sidingPage">
      <Header />

      <section className="sidingHero">
        <div className="sidingHeroInner">
          <div className="sidingHeroCopy">
            <span className="fenceEyebrow">Exterior finishing</span>
            <h1>Siding That Sharpens and Protects the Exterior</h1>
            <p>
              Siding repair and replacement planned around weather exposure, clean details,
              and the overall look of the property.
            </p>
            <button className="rBtn" onClick={() => navigate("/contact")}>
              REQUEST AN ESTIMATE
            </button>
          </div>
          <div className="sidingHeroImage" aria-hidden="true">
            <img src={`${pubUrl}/images/residential-hero.png`} alt="" />
          </div>
        </div>
      </section>

      <section className="sidingOverview">
        <div className="container sidingOverviewGrid">
          <div className="sidingOverviewTitle">
            <h2>
              <span className="rAccent" aria-hidden="true" />
              Exterior work depends on the details
            </h2>
          </div>
          <div className="sidingOverviewText">
            <p>
              Siding is both a finish and a weather-facing system. When it is damaged,
              loose, poorly detailed, or past its service life, the exterior can start to
              look tired while also becoming more vulnerable at transitions.
            </p>
            <p>
              We approach siding work by looking at the full exterior condition: where water
              moves, where panels terminate, how trim is handled, and what needs to be corrected
              for the finished work to hold up.
            </p>
          </div>
        </div>
      </section>

      <section className="sidingServices">
        <div className="container">
          <div className="fenceSectionHead">
            <h2>Siding Services</h2>
            <p>Repair, replacement, and finishing work for cleaner, stronger exterior walls.</p>
          </div>

          <div className="sidingServiceGrid">
            {sidingServices.map((item) => (
              <article className="sidingServiceCard" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sidingPriorities">
        <div className="container sidingPrioritiesGrid">
          <div className="sidingPriorityMedia" aria-hidden="true">
            <img src={`${pubUrl}/images/landing/house.png`} alt="" />
          </div>
          <div className="sidingPriorityCopy">
            <h2>What Gets Checked</h2>
            <div className="sidingPriorityList">
              {priorities.map((item) => (
                <div className="sidingPriorityItem" key={item}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sidingCta">
        <div className="sidingCtaInner">
          <h2>Ready to review your siding?</h2>
          <p>
            We can look at the exterior, identify what needs repair or replacement, and define
            a practical scope before work begins.
          </p>
          <button className="rBtn" onClick={() => navigate("/contact")}>
            REQUEST AN ESTIMATE
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
